import type { GameState } from '@thicket/core';
import { html, nothing } from 'lit-html';
import { NetworkError, request } from '../api.js';
import { app, toast, update } from './app.js';
import { remindersView } from './reminders.js';
import { starterSettings } from './starter.js';
import { startTour } from './tour.js';

let importing = false;
let pauseUntil = '';
/** The delete-account confirm: closed, open with what's typed so far, or sending. */
let deleting: { typed: string; busy: boolean } | null = null;
const DELETE_PHRASE = 'delete my account';
const zones = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return [] as string[];
  }
})();

async function importFile(file: File) {
  importing = true;
  update();
  try {
    const data = JSON.parse(await file.text());
    const r = await request<{ created: number; updated: number }>(fetch, '/api/import', { method: 'POST', body: JSON.stringify({ data }) });
    toast(`Brought in ${r.created} new task${r.created === 1 ? '' : 's'}${r.updated ? ` and updated ${r.updated}` : ''}`, 4000);
    await app.store.refresh();
  } catch (err) {
    toast(err instanceof SyntaxError ? "That file isn't JSON. Download the User Data export again." : (err as Error).message, 5000);
  } finally {
    importing = false;
    update();
  }
}

async function deleteAccount() {
  if (!deleting) return;
  deleting.busy = true;
  update();
  try {
    await request(fetch, '/api/account', { method: 'DELETE', body: JSON.stringify({ confirm: DELETE_PHRASE }) });
  } catch (err) {
    deleting.busy = false;
    update();
    toast(err instanceof NetworkError ? 'Deleting needs a connection. Nothing was deleted.' : (err as Error).message, 5000);
    return;
  }
  // Nothing of the account stays on this device either: the saved garden, the offline queue, the tour.
  try {
    for (const key of Object.keys(localStorage)) if (key.startsWith('thicket.')) localStorage.removeItem(key);
  } catch { /* storage blocked: nothing saved */ }
  location.assign('/signin');
}

function deleteView() {
  if (!deleting) {
    return html`<div class="actions"><button class="danger" @click=${() => { deleting = { typed: '', busy: false }; update(); }}>Delete my account</button></div>`;
  }
  const ready = deleting.typed.trim().toLowerCase() === DELETE_PHRASE;
  return html`<div class="box banner warn form" role="group" aria-labelledby="delete-title">
    <p style="margin:0" id="delete-title"><b>Delete your account and everything in it?</b></p>
    <p style="margin:0">Your tasks, garden, Field Journal and history are removed for good, Claude is disconnected, and every device is signed out.
      There's no undo, so download your data first if you'd like a copy.</p>
    <label class="field"><span>Type <i>${DELETE_PHRASE}</i> to confirm</span>
      <input type="text" autocomplete="off" autocapitalize="off" spellcheck="false" .value=${deleting.typed} ?disabled=${deleting.busy}
        @input=${(e: Event) => { deleting!.typed = (e.target as HTMLInputElement).value; update(); }}></label>
    <div class="actions">
      <button class="danger" ?disabled=${!ready || deleting.busy} @click=${() => void deleteAccount()}>${deleting.busy ? 'Deleting…' : 'Delete everything'}</button>
      <button ?disabled=${deleting.busy} @click=${() => { deleting = null; update(); }}>Keep my account</button>
    </div>
  </div>`;
}

export function settingsView(s: GameState) {
  const p = s.profile;
  const archived = s.tasks.filter((t) => t.archivedAt);
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const hourLabel = (h: number) => (h === 0 ? 'Midnight' : h === 12 ? 'Noon' : h < 12 ? `${h} am` : `${h - 12} pm`);
  return html`
    <section class="box form">
      <h2>Your day</h2>
      <label class="field">Time zone
        <select @change=${(e: Event) => app.store.act({ type: 'updateSettings', timezone: (e.target as HTMLSelectElement).value })}>
          ${(zones.includes(p.timezone) ? zones : [p.timezone, ...zones]).map((z) => html`<option ?selected=${z === p.timezone} value=${z}>${z.replace(/_/g, ' ')}</option>`)}
        </select></label>
      <label class="field">New day starts at
        <select @change=${(e: Event) => app.store.act({ type: 'updateSettings', dayStartHour: Number((e.target as HTMLSelectElement).value) })}>
          ${hours.map((h) => html`<option ?selected=${h === p.dayStartHour} value=${h}>${hourLabel(h)}</option>`)}
        </select></label>
      <p class="small muted" style="margin:0">If you often finish things after midnight, start your day later.</p>
    </section>

    ${remindersView()}

    ${starterSettings(s)}

    <section class="box form" id="rest">
      <h2>Rest</h2>
      ${p.pause
        ? html`<p style="margin:0">Resting${p.pause.until ? ` until ${p.pause.until}` : ''}. Nothing changes while you rest.</p>
            <div class="actions"><button class="primary" @click=${() => app.store.act({ type: 'resume' })}>Resume</button></div>`
        : html`<p style="margin:0">Pausing keeps every streak and flower as it is. Lighter options: rest a single daily from its edit screen, or give it fewer days a week.</p>
            <label class="field">Back on (optional)<input type="date" .value=${pauseUntil} @input=${(e: Event) => (pauseUntil = (e.target as HTMLInputElement).value)}></label>
            <div class="actions"><button @click=${() => app.store.act(pauseUntil ? { type: 'pause', until: pauseUntil } : { type: 'pause' })}>
              ${pauseUntil ? `Rest until ${pauseUntil}` : 'Rest until I come back'}</button></div>`}
    </section>

    <section class="box form">
      <h2>Bring in your old tasks</h2>
      <p style="margin:0">Coming from another habit app? Download its <b>User Data</b> export as JSON and choose that file here.
        Your dailies arrive as trees sized by your history, habits as raised-bed plants, and to-dos as buds. Importing again updates them instead of making copies.</p>
      <label class="field">Export file
        <input type="file" accept=".json,application/json" ?disabled=${importing}
          @change=${(e: Event) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) void importFile(f); }}></label>
      ${importing ? html`<p class="muted" style="margin:0">Bringing your tasks in…</p>` : nothing}
    </section>

    <section class="box">
      <div class="section-head"><h2>Archived</h2><span class="muted small">${archived.length}</span></div>
      <ul class="list">${archived.length ? archived.map((t) => html`<li class="row" style="min-height:44px">
        <span class="body"><span class="title">${t.text}</span><span class="meta">${t.type}</span></span>
        <button @click=${() => app.store.act({ type: 'unarchiveTask', id: t.id })}>Restore</button></li>`)
        : html`<li class="empty">Archived tasks show up here.</li>`}</ul>
    </section>

    <section class="box form" id="help">
      <h2>Help</h2>
      <p style="margin:0">A quick walk through Thicket: how dailies, habits and to-dos grow, the garden, the Field Journal and resting.</p>
      <div class="actions"><button class="primary" id="tour-btn" @click=${() => startTour()}>Take the tour</button></div>
    </section>

    <section class="box form">
      <h2>Account</h2>
      <p style="margin:0">Passkeys, connected apps like Claude, signed-in devices and signing out live on your account page.</p>
      <div class="actions"><a href="/account"><button>Open account page</button></a>
        <a href="/api/export" download><button>Download my data</button></a></div>
      ${deleteView()}
      <p class="small muted" style="margin:0"><a href="/privacy">Privacy policy</a> · <a href="/terms">Terms of use</a> · <a href="/support">Support</a></p>
    </section>
    ${app.canDev && !app.dev ? html`<section class="box form">
      <h2>Dev mode</h2>
      <p style="margin:0">Play a sandbox copy of your garden with the RPG Lab's art: skip days, grow trees, set up any scene. Nothing in it changes your real garden.</p>
      <div class="actions"><a href="/dev/garden"><button class="primary">Open dev mode</button></a></div>
    </section>` : nothing}
  `;
}
